import { usePage } from "@inertiajs/react"
import { type ReactNode, createContext, useMemo } from "react"

import type {
  ActingAsAgencyContext,
  ActingAsContext,
  AvailableBrand,
  CurrentAccount,
  CurrentBrand,
  CurrentSession,
  CurrentUser,
} from "@/types"

/**
 * Narrow, stable view of the Inertia shared-props tenancy contract.
 *
 * The provider is a thin wrapper: source of truth is the server (Inertia
 * shared props rewritten on every visit). We do **not** mirror or persist
 * client-side — the server is authoritative for tenancy.
 */
export interface CurrentContextValue {
  currentUser: CurrentUser | null
  currentBrand: CurrentBrand | null
  currentAccount: CurrentAccount
  currentSession: CurrentSession | null
  availableBrands: AvailableBrand[]
  /**
   * OKS-24 — `users.active_brand_id` for creators (null otherwise). Drives
   * the wizard's server-side dispatch + the switcher's "Active crew"
   * highlight. FE never mutates this — POST to `currentBrandPath()` is the
   * only write path.
   */
  activeBrandId: number | null
  /**
   * OKS-36 — Unread-notifications count for the bell badge. Integer 0..99
   * (BE-capped via `Notification::UNREAD_COUNT_CAP`). FE renders `9+`
   * over 9. Refreshed via partial reload from `usePollUnreadCount`.
   */
  unreadNotificationsCount: number
  /**
   * OKS-484 (A) — "N to submit" count for the Missions bottom-tab badge.
   * Integer 0..99 (BE-capped via `TO_SUBMIT_COUNT_CAP`). Creator-only +
   * brand-scoped; `0` (the default) → the FE renders no badge. Refreshed via
   * partial reload alongside the other shell counts.
   */
  toSubmitCount: number
  /**
   * OKS-570 — Learn bottom-tab gate. `true` iff the active brand has ≥1
   * published masterclass (`Masterclass.state_published.for_brand`). Creator-
   * only + brand-scoped; the server OMITS the `has_masterclass_content` prop on
   * non-creator shells, so it defaults to `false` here. Re-evaluated per visit
   * (brand switch re-runs the server lambda). Drives whether `buildCreatorTabs`
   * pushes the Learn slot.
   */
  hasMasterclassContent: boolean
  /**
   * OKS-312 — Admin "act as brand" context. `null` on every non-acting
   * request (including admins not currently in act-as). Drives
   * `<ActingAsBanner>` rendering and the composer "disabled while
   * acting" UX in the messaging surface. FE never mutates — only the
   * enter (`POST /admin/act_as_brand`) and exit (`DELETE
   * /admin/act_as_brand`) endpoints can flip it.
   */
  actingAs: ActingAsContext | null
  /**
   * OKS-683 — Admin "act as agency" context. `null` on every non-acting
   * request (including admins not currently in act-as-agency). Drives
   * `<ActingAsAgencyBanner>` rendering. Mutually exclusive with
   * `actingAs` — only ever one of the pair is non-null, so only one
   * banner renders. FE never mutates — only the enter
   * (`POST /admin/act_as_agency`) and exit (`DELETE /admin/act_as_agency`)
   * endpoints flip it.
   */
  actingAsAgency: ActingAsAgencyContext | null
  /**
   * OKS-532 — external Privacy Policy URL for the app-wide
   * `<AnalyticsNoticeBanner>` link. Always a non-blank string (server
   * resolves the `PRIVACY_URL` env override or the oks.studio default).
   * Present on every request; FE never mutates.
   */
  privacyUrl: string
}

const EMPTY_ACCOUNT: CurrentAccount = { type: null, id: null, name: null }

export const CurrentContext = createContext<CurrentContextValue | null>(null)

interface CurrentContextProviderProps {
  children: ReactNode
}

/**
 * Subscribes to `usePage().props` once per visit and exposes a typed,
 * narrowed slice via `useCurrentContext()`. No local state, no caching.
 */
export function CurrentContextProvider({
  children,
}: CurrentContextProviderProps) {
  const {
    currentUser = null,
    currentBrand = null,
    currentAccount = EMPTY_ACCOUNT,
    currentSession = null,
    availableBrands = [],
    active_brand_id: activeBrandId = null,
    unread_notifications_count: unreadNotificationsCount = 0,
    to_submit_count: toSubmitCount = 0,
    has_masterclass_content: hasMasterclassContent = false,
    acting_as: actingAs = null,
    acting_as_agency: actingAsAgency = null,
    privacy_url: privacyUrl = "",
  } = usePage().props

  const value = useMemo<CurrentContextValue>(
    () => ({
      currentUser,
      currentBrand,
      currentAccount,
      currentSession,
      availableBrands,
      activeBrandId,
      unreadNotificationsCount,
      toSubmitCount,
      hasMasterclassContent,
      actingAs,
      actingAsAgency,
      privacyUrl,
    }),
    [
      currentUser,
      currentBrand,
      currentAccount,
      currentSession,
      availableBrands,
      activeBrandId,
      unreadNotificationsCount,
      toSubmitCount,
      hasMasterclassContent,
      actingAs,
      actingAsAgency,
      privacyUrl,
    ],
  )

  return (
    <CurrentContext.Provider value={value}>{children}</CurrentContext.Provider>
  )
}
